import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import BrandLogo from "../components/BrandLogo";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import FinancialIndexCards from "../components/FinancialIndexCards";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { trackEvent, trackPageView } from "../lib/analytics";
import { 
  ArrowLeft, 
  ArrowRight, 
  Save, 
  CheckCircle2,
  AlertCircle,
  LogOut,
  Landmark,
  Upload,
  FileDown,
  Loader2,
  Trash2
} from "lucide-react";

const AREAS = [
  "Financeiro",
  "Planejamento Estratégico",
  "Gestão Comercial",
  "Recursos Humanos",
  "Administração",
  "Produção",
  "Logística",
  "Tecnologia",
  "Jurídico",
  "Marketing",
  "Compras",
  "NR-1 Gestão de Riscos Ocupacionais",
  "Visão de Futuro"
];

const getAreaLabel = (area) => {
  if (area === "Tecnologia") return "Tecnologia/IA";
  return area;
};

const ANSWER_OPTIONS = [
  { value: "A", label: "Excelente", description: "Atende plenamente aos requisitos" },
  { value: "B", label: "Bom", description: "Atende com pequenas oportunidades" },
  { value: "C", label: "Satisfatório", description: "Com oportunidades de melhoria" },
  { value: "D", label: "Insuficiente", description: "Necessita melhorias significativas" },
  { value: "E", label: "Inadequado", description: "Não atende aos requisitos" },
  { value: "NA", label: "Não se aplica", description: "Não é relevante para esta empresa" }
];

const DiagnosticWizard = () => {
  const { diagnosticId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentAreaIndex, setCurrentAreaIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [financialAssessment, setFinancialAssessment] = useState(null);
  const [financialFiles, setFinancialFiles] = useState([]);
  const [financialNotes, setFinancialNotes] = useState("");
  const [uploadingFinancial, setUploadingFinancial] = useState(false);
  const [reanalyzingFinancial, setReanalyzingFinancial] = useState(false);
  const areaTabRefs = useRef([]);

  const fetchData = useCallback(async () => {
    try {
      const [questionsRes, diagnosticRes, answersRes, financialRes] = await Promise.all([
        axios.get(`${API}/questions`, { withCredentials: true }),
        axios.get(`${API}/diagnostics/${diagnosticId}`, { withCredentials: true }),
        axios.get(`${API}/diagnostics/${diagnosticId}/answers`, { withCredentials: true }),
        axios.get(`${API}/diagnostics/${diagnosticId}/financial-assessment`, { withCredentials: true }).catch(() => ({ data: { exists: false } }))
      ]);
      
      setQuestions(questionsRes.data.questions || []);
      setDiagnostic(diagnosticRes.data);
      
      // Convert answers array to object keyed by question_id
      const answersMap = {};
      (answersRes.data.answers || []).forEach(a => {
        answersMap[a.question_id] = {
          answer_option: a.answer_option,
          note_text: a.note_text || ""
        };
      });
      setAnswers(answersMap);
      setFinancialAssessment(financialRes.data?.exists ? financialRes.data : null);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar diagnóstico");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [diagnosticId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    trackPageView("diagnostic_wizard_view", {
      path: `/diagnostic/${diagnosticId}`,
      metadata: { diagnostic_id: diagnosticId },
    });
  }, [diagnosticId]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const activeTab = areaTabRefs.current[currentAreaIndex];
    if (activeTab?.scrollIntoView) {
      activeTab.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [currentAreaIndex]);

  const fetchFinancialAssessment = async () => {
    try {
      const response = await axios.get(`${API}/diagnostics/${diagnosticId}/financial-assessment`, { withCredentials: true });
      setFinancialAssessment(response.data?.exists ? response.data : null);
    } catch (error) {
      setFinancialAssessment(null);
    }
  };

  const uploadFinancialAssessment = async () => {
    if (!financialFiles.length) {
      toast.error("Selecione ao menos um arquivo (PDF, XLS ou XLSX)");
      return;
    }

    const formData = new FormData();
    financialFiles.forEach((file) => formData.append("files", file));
    if (financialNotes.trim()) formData.append("notes", financialNotes.trim());

    setUploadingFinancial(true);
    try {
      await axios.post(`${API}/diagnostics/${diagnosticId}/financial-assessment`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Avaliação financeira processada!");
      setFinancialFiles([]);
      setFinancialNotes("");
      fetchFinancialAssessment();
    } catch (error) {
      console.error("Error uploading financial assessment:", error);
      toast.error(error.response?.data?.detail || "Erro ao processar avaliação financeira");
    } finally {
      setUploadingFinancial(false);
    }
  };

  const downloadFinancialFile = async (fileId, originalName) => {
    try {
      const response = await axios.get(`${API}/diagnostics/${diagnosticId}/financial-assessment/files/${fileId}`, {
        withCredentials: true,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", originalName || `arquivo_financeiro_${fileId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading financial file:", error);
      toast.error("Erro ao baixar arquivo financeiro");
    }
  };

  const deleteFinancialFile = async (fileId) => {
    try {
      await axios.delete(`${API}/diagnostics/${diagnosticId}/financial-assessment/files/${fileId}`, {
        withCredentials: true
      });
      toast.success("Arquivo removido");
      fetchFinancialAssessment();
    } catch (error) {
      console.error("Error deleting financial file:", error);
      toast.error(error.response?.data?.detail || "Erro ao excluir arquivo");
    }
  };

  const reanalyzeFinancial = async () => {
    setReanalyzingFinancial(true);
    try {
      await axios.post(`${API}/diagnostics/${diagnosticId}/financial-assessment/reanalyze`, {}, {
        withCredentials: true
      });
      toast.success("Reanálise concluída");
      fetchFinancialAssessment();
    } catch (error) {
      console.error("Error reanalyzing financial:", error);
      toast.error(error.response?.data?.detail || "Erro ao reanalisar");
    } finally {
      setReanalyzingFinancial(false);
    }
  };

  const currentArea = AREAS[currentAreaIndex];
  const areaQuestions = questions.filter(q => q.area === currentArea);
  
  const getAreaProgress = (area) => {
    const areaQs = questions.filter(q => q.area === area);
    const answered = areaQs.filter(q => answers[q.id]?.answer_option).length;
    return areaQs.length > 0 ? (answered / areaQs.length) * 100 : 0;
  };

  const totalProgress = () => {
    const totalAnswered = Object.values(answers).filter(a => a?.answer_option).length;
    return questions.length > 0 ? (totalAnswered / questions.length) * 100 : 0;
  };

  const handleAnswerChange = (questionId, option) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        answer_option: option
      }
    }));
  };

  const handleNoteChange = (questionId, note) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        note_text: note
      }
    }));
  };

  const saveProgress = async () => {
    setSaving(true);
    
    try {
      const answersToSave = Object.entries(answers)
        .filter(([_, a]) => a?.answer_option)
        .map(([questionId, answer]) => {
          const question = questions.find(q => q.id === parseInt(questionId));
          return {
            question_id: parseInt(questionId),
            area: question?.area || "",
            answer_option: answer.answer_option,
            note_text: answer.note_text || ""
          };
        });
      
      await axios.post(`${API}/diagnostics/${diagnosticId}/answers`, answersToSave, { withCredentials: true });
      toast.success("Progresso salvo!");
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Erro ao salvar progresso");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    const unanswered = questions.filter(q => !answers[q.id]?.answer_option);
    if (unanswered.length > 0) {
      toast.error(`Ainda faltam ${unanswered.length} perguntas para responder`);
      return;
    }
    
    setSubmitting(true);
    
    try {
      trackEvent("diagnostic_submit_attempt", {
        path: `/diagnostic/${diagnosticId}`,
        metadata: { diagnostic_id: diagnosticId },
      });
      // Save all answers first
      const answersToSave = Object.entries(answers)
        .filter(([_, a]) => a?.answer_option)
        .map(([questionId, answer]) => {
          const question = questions.find(q => q.id === parseInt(questionId));
          return {
            question_id: parseInt(questionId),
            area: question?.area || "",
            answer_option: answer.answer_option,
            note_text: answer.note_text || ""
          };
        });
      
      await axios.post(`${API}/diagnostics/${diagnosticId}/answers`, answersToSave, { withCredentials: true });
      
      // Submit
      const response = await axios.post(`${API}/diagnostics/${diagnosticId}/submit`, {}, { withCredentials: true });

      if (response.data?.payment_required) {
        trackEvent("payment_page_redirect", {
          path: `/payment/diagnostic/${diagnosticId}`,
          metadata: { diagnostic_id: diagnosticId, payment_id: response.data?.payment?.payment_id },
        });
        toast.success("Diagnóstico finalizado. Agora conclua o pagamento para liberar o resultado.");
        navigate(`/payment/diagnostic/${diagnosticId}`);
        return;
      }

      trackEvent("diagnostic_submit_success", {
        path: `/results/${diagnosticId}`,
        metadata: { diagnostic_id: diagnosticId },
      });
      toast.success("Diagnóstico concluído!");
      navigate(`/results/${diagnosticId}`);
    } catch (error) {
      console.error("Error submitting:", error);
      trackEvent("diagnostic_submit_failure", {
        path: `/diagnostic/${diagnosticId}`,
        metadata: { diagnostic_id: diagnosticId, reason: error.response?.data?.detail || "unknown" },
      });
      toast.error(error.response?.data?.detail || "Erro ao submeter diagnóstico");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/dashboard" className="flex items-center">
            <BrandLogo size="md" className="max-w-[220px]" />
          </Link>
          
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={saveProgress}
              disabled={saving}
              data-testid="save-progress-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Salvando..." : "Salvar"}
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              data-testid="wizard-logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Progress Overview */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-serif font-bold text-navy">
              {diagnostic?.company_name}
            </h1>
            <span className="text-sm font-mono text-slate-600">
              {Math.round(totalProgress())}% completo
            </span>
          </div>
          <Progress value={totalProgress()} className="h-2" />
        </div>

        {/* Area Tabs */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex gap-2 min-w-max pb-2">
            {AREAS.map((area, index) => {
              const progress = getAreaProgress(area);
              const isComplete = progress === 100;
              const isCurrent = index === currentAreaIndex;
              
              return (
                <button
                  key={area}
                  ref={(node) => {
                    areaTabRefs.current[index] = node;
                  }}
                  onClick={() => setCurrentAreaIndex(index)}
                  className={`
                    px-4 py-2 rounded-sm text-sm font-medium transition-all
                    flex items-center gap-2 whitespace-nowrap
                    ${isCurrent 
                      ? "bg-navy text-white" 
                      : isComplete 
                        ? "bg-emerald-100 text-emerald-800" 
                        : "bg-white text-slate-600 hover:bg-slate-100"
                    }
                  `}
                  data-testid={`area-tab-${index}`}
                >
                  {isComplete && <CheckCircle2 className="h-4 w-4" />}
                  {index + 1}. {getAreaLabel(area).length > 15 ? getAreaLabel(area).substring(0, 15) + "..." : getAreaLabel(area)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Current Area Questions */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-navy flex items-center gap-3">
              <span className="w-8 h-8 bg-navy/10 rounded flex items-center justify-center text-sm font-mono">
                {currentAreaIndex + 1}
              </span>
              {getAreaLabel(currentArea)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            {areaQuestions.map((question, qIndex) => {
              const currentAnswer = answers[question.id]?.answer_option;
              
              return (
                <div key={question.id} className="border-b border-slate-100 pb-8 last:border-0 last:pb-0">
                  <div className="mb-4">
                    <span className="text-sm font-mono text-slate-500 mb-1 block">
                      Pergunta {question.id}
                    </span>
                    <p className="text-navy font-medium">{question.text}</p>
                  </div>
                  
                  <RadioGroup
                    value={currentAnswer || ""}
                    onValueChange={(value) => handleAnswerChange(question.id, value)}
                    className="grid grid-cols-2 md:grid-cols-3 gap-3"
                  >
                    {ANSWER_OPTIONS.map((option) => (
                      <div key={option.value}>
                        <RadioGroupItem
                          value={option.value}
                          id={`q${question.id}-${option.value}`}
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor={`q${question.id}-${option.value}`}
                          className={`
                            flex flex-col p-3 rounded-sm border cursor-pointer transition-all
                            peer-data-[state=checked]:border-navy peer-data-[state=checked]:bg-navy/5
                            hover:border-slate-300
                            ${option.value === "NA" ? "border-dashed" : ""}
                          `}
                          data-testid={`q${question.id}-option-${option.value}`}
                        >
                          <span className="font-medium text-sm">{option.label}</span>
                          <span className="text-xs text-slate-500">{option.description}</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                  
                  <div className="mt-4">
                    <Label htmlFor={`note-${question.id}`} className="text-sm text-slate-600">
                      Observações (opcional)
                    </Label>
                    <Textarea
                      id={`note-${question.id}`}
                      value={answers[question.id]?.note_text || ""}
                      onChange={(e) => handleNoteChange(question.id, e.target.value)}
                      placeholder="Adicione observações sobre esta resposta..."
                      className="mt-1 h-20"
                      data-testid={`q${question.id}-note`}
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Financial Assessment (Optional) */}
        <Card className="mb-8 border-0 shadow-md">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-navy flex items-center gap-2">
              <Landmark className="h-5 w-5" />
              Avaliação Financeira Opcional (DRE/Balanço)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded border border-slate-200 bg-slate-50 p-4 space-y-3">
              <div>
                <Label className="text-sm text-slate-700">Arquivos financeiros (envie DRE, Balanço ou os dois juntos)</Label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.xls,.xlsx,.xlsm,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => setFinancialFiles(Array.from(e.target.files || []))}
                  className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:rounded file:border-0 file:bg-navy file:px-3 file:py-1.5 file:text-white hover:file:bg-navy/90"
                />
                {financialFiles.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">{financialFiles.length} arquivo(s) selecionado(s)</p>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Se a empresa tiver DRE e Balanço, a IA combina os dois arquivos na mesma leitura. Se enviar apenas um, a análise considera somente o arquivo disponível.
                </p>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Observações opcionais</Label>
                <Textarea
                  value={financialNotes}
                  onChange={(e) => setFinancialNotes(e.target.value)}
                  placeholder="Ex.: queremos foco em caixa, margem e endividamento"
                  className="mt-1 h-20"
                />
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={uploadFinancialAssessment}
                  disabled={uploadingFinancial}
                  className="bg-navy hover:bg-navy/90"
                  data-testid="wizard-upload-financial-btn"
                >
                  {uploadingFinancial ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Enviar para Avaliação IA
                    </>
                  )}
                </Button>
                {diagnostic?.status === "submitted" && (
                  <Link to={`/results/${diagnosticId}`}>
                    <Button variant="outline">Ver Resultado</Button>
                  </Link>
                )}
              </div>
            </div>

            {!financialAssessment ? (
              <p className="text-sm text-slate-500">Nenhuma avaliação financeira enviada ainda.</p>
            ) : (
              <div className="space-y-4">
                {financialAssessment.analysis_status === "pending_payment" && (
                  <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    Os arquivos já foram recebidos. A leitura de DRE/Balanço ficará disponível assim que o pagamento deste diagnóstico for confirmado.
                  </div>
                )}
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Estado financeiro</p>
                    <Badge
                      className={`mt-2 ${
                        financialAssessment.analysis_status === "pending_payment"
                          ? "bg-amber-100 text-amber-800"
                          :
                        financialAssessment.estado_financeiro === "Saudável"
                          ? "bg-emerald-100 text-emerald-800"
                          : financialAssessment.estado_financeiro === "Atenção"
                          ? "bg-amber-100 text-amber-800"
                          : financialAssessment.estado_financeiro === "Crítico"
                          ? "bg-red-100 text-red-800"
                          : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {financialAssessment.estado_financeiro || "Indeterminado"}
                    </Badge>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Leitura priorizada</p>
                    <p className="mt-2 text-sm font-semibold text-navy">{financialAssessment.tipo_analise || "Financeira"}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Quantidade de arquivos</p>
                    <p className="mt-2 text-sm font-semibold text-navy">{(financialAssessment.files || []).length} arquivo(s)</p>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <h4 className="font-medium text-navy mb-2">Visão Executiva</h4>
                  <p className="text-sm text-slate-700 whitespace-pre-line">
                    {financialAssessment.resumo_executivo || financialAssessment.resumo}
                  </p>
                </div>

                {!!financialAssessment.leitura_empresarial?.length && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <h4 className="font-medium text-navy mb-2">O que isso significa para a empresa</h4>
                    <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                      {financialAssessment.leitura_empresarial.map((item, idx) => (
                        <li key={`wizard-fin-business-read-${idx}`}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <h4 className="font-medium text-navy mb-1">Leitura Técnica Resumida</h4>
                  <p className="text-sm text-slate-600 whitespace-pre-line">{financialAssessment.resumo}</p>
                </div>

                <div>
                  <h4 className="font-medium text-navy mb-2">Índices de Liquidez e Estrutura</h4>
                  <FinancialIndexCards items={financialAssessment.indices_financeiros || []} prefix="wizard-fin-index" />
                </div>

                {financialAssessment.analysis_status !== "pending_payment" && (
                <div>
                  <h4 className="font-medium text-navy mb-1">Dicas Primárias</h4>
                  <ul className="list-disc pl-5 text-sm text-slate-600 space-y-1">
                    {(financialAssessment.dicas_primarias || []).map((tip, idx) => (
                      <li key={`wizard-fin-tip-${idx}`}>{tip}</li>
                    ))}
                  </ul>
                </div>
                )}

                <div className="rounded border border-blue-100 bg-blue-50 p-3">
                  <p className="text-sm text-blue-900">{financialAssessment.recomendacao_consultoria}</p>
                </div>

                <div className="space-y-2">
                  {financialAssessment.analysis_status !== "pending_payment" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mb-2"
                      onClick={reanalyzeFinancial}
                      disabled={reanalyzingFinancial}
                    >
                      {reanalyzingFinancial ? "Reanalisando..." : "Reanalisar IA"}
                    </Button>
                  )}
                  {(financialAssessment.files || []).map((file) => (
                    <div key={file.file_id} className="flex items-center justify-between rounded border border-slate-200 bg-white p-2">
                      <div>
                        <p className="text-sm text-slate-700">{file.original_name}</p>
                        <p className="text-xs text-slate-500">{Math.round((file.size_bytes || 0) / 1024)} KB</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => downloadFinancialFile(file.file_id, file.original_name)}>
                          <FileDown className="h-4 w-4 mr-1" />
                          Baixar
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => deleteFinancialFile(file.file_id)}>
                          <Trash2 className="h-4 w-4 mr-1" />
                          Excluir
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentAreaIndex(Math.max(0, currentAreaIndex - 1))}
            disabled={currentAreaIndex === 0}
            data-testid="prev-area-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Área Anterior
          </Button>
          
          {currentAreaIndex === AREAS.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-gold hover:bg-gold/90 text-navy font-semibold"
              data-testid="submit-diagnostic-btn"
            >
              {submitting ? "Enviando..." : "Concluir Diagnóstico"}
              <CheckCircle2 className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentAreaIndex(Math.min(AREAS.length - 1, currentAreaIndex + 1))}
              className="bg-navy hover:bg-navy/90"
              data-testid="next-area-btn"
            >
              Próxima Área
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default DiagnosticWizard;
